import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "What services do you offer?",
  "How much is a full detail?",
  "Where are you located?",
];

// Render **bold** and links from the assistant text
const formatText = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*|https?:\/\/[^\s)]+)/g);
  return parts.map((part, i) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) {
      return (
        <strong key={i} className="font-semibold text-white">
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (/^https?:\/\//.test(part)) {
      let label = "open link";
      if (part.includes("booksy")) label = "book on Booksy";
      else if (part.includes("instagram")) label = "Instagram";
      else if (part.includes("maps") || part.includes("goo.gl")) label = "view on map";
      return (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2 text-white break-all"
        >
          {label}
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi! I'm the Marez AI Concierge. Ask me about our detailing services, pricing, or how to book. How can I help your car today?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(
    typeof window !== "undefined" ? localStorage.getItem("marez_chat_session") : null
  );
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        message: content,
        session_id: sessionId.current,
      });
      sessionId.current = res.data.session_id;
      localStorage.setItem("marez_chat_session", res.data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Sorry, I'm having trouble right now. Please call or text us at (510) 303-0544.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-toggle"
        onClick={() => setOpen((v) => !v)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 2.4, type: "spring", stiffness: 200 }}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="btn-3d fixed bottom-24 right-6 z-[900] h-14 w-14 rounded-full bg-[#fafafa] text-[#050505] flex items-center justify-center"
        aria-label="Open chat assistant"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="fixed bottom-40 right-6 z-[900] w-[calc(100vw-3rem)] sm:w-[380px] h-[540px] max-h-[70vh] rounded-3xl border border-white/10 bg-[#0a0a0a] shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 bg-[#111111]">
              <div className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center">
                <Sparkles size={16} className="text-white" />
              </div>
              <div>
                <p className="font-heading font-semibold text-white leading-tight">
                  Marez AI Concierge
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">
                  ● Online
                </p>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`chat-msg-${m.role}`}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#fafafa] text-[#050505] rounded-br-sm"
                        : "bg-[#1a1a1a] text-zinc-200 rounded-bl-sm border border-white/5"
                    }`}
                  >
                    {m.role === "assistant" ? formatText(m.content) : m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-[#1a1a1a] border border-white/5 rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1.5">
                    {[0, 1, 2].map((d) => (
                      <motion.span
                        key={d}
                        animate={{ opacity: [0.3, 1, 0.3] }}
                        transition={{ duration: 1, repeat: Infinity, delay: d * 0.2 }}
                        className="h-2 w-2 rounded-full bg-zinc-400"
                      />
                    ))}
                  </div>
                </div>
              )}

              {messages.length <= 1 && !loading && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid="chat-suggestion"
                      onClick={() => send(s)}
                      className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-zinc-300 hover:bg-white/10 hover:text-white transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input */}
            <div className="p-3 border-t border-white/10 bg-[#111111]">
              <div className="flex items-center gap-2">
                <input
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ask about services or pricing..."
                  className="flex-1 bg-[#0a0a0a] border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/40"
                />
                <button
                  data-testid="chat-send"
                  onClick={() => send()}
                  disabled={loading || !input.trim()}
                  className="h-10 w-10 shrink-0 rounded-full bg-[#fafafa] text-[#050505] flex items-center justify-center hover:bg-white transition-colors disabled:opacity-50"
                >
                  <Send size={17} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
